import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertTriangle } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Combobox } from '@/shared/components/ui/combobox';
import { Switch } from '@/shared/components/ui/switch';
import {
  createCuentaContable,
  listAllCuentasContables,
  softDeleteCuentaContable,
  updateCuentaContable,
  type CuentaContableItem,
} from '@/shared/api/controllers/cuentasContables';
import { listActiveCategorias } from '@/shared/api/controllers/categorias';
import { listAllEfSubitems } from '@/shared/api/controllers/efSubitems';
import { invalidateAbmRelatedQueries } from '@/shared/api/abmCacheInvalidation';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EntityViewProps } from '../../types';

const QUERY_KEY = ['configuracion', 'cuentas-contables'] as const;

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: CuentaContableItem }
  | { kind: 'delete'; item: CuentaContableItem };

const schema = z.object({
  cuentaContable: z.string().trim().min(1, 'Ingresá la cuenta contable'),
  categoria: z.string().optional(),
  impactaStock: z.boolean(),
  nroSerie: z.boolean(),
  /** ef_subitems.id as string; '' = sin clasificación EF. */
  efSubItemId: z.string().optional(),
});
type Values = z.infer<typeof schema>;

const empty: Values = {
  cuentaContable: '',
  categoria: '',
  impactaStock: false,
  nroSerie: false,
  efSubItemId: '',
};

function itemToForm(item: CuentaContableItem): Values {
  return {
    cuentaContable: item.fields.CuentaContable_CC ?? '',
    categoria: item.fields.Categoria_CC ?? '',
    impactaStock: item.fields.ImpactaStock_CC === 'SI',
    nroSerie: item.fields.NroSerie_CC === 'SI',
    efSubItemId: item.fields.EFSubItemId_CC ?? '',
  };
}

export function CuentasContablesEntity({
  search,
  filterValue,
  onStatus,
  openCreate,
  onCreateHandled,
}: EntityViewProps) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...QUERY_KEY, activeCountry.code] as readonly unknown[],
    [activeCountry.code],
  );
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: scopedKey,
    queryFn: listAllCuentasContables,
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });

  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows].sort((a, b) =>
      (a.fields.CuentaContable_CC ?? '').localeCompare(b.fields.CuentaContable_CC ?? '', 'es'),
    );
    if (filterValue) {
      sorted = sorted.filter((r) => r.fields.Status_CC === filterValue);
    }
    if (!q) return sorted;
    return sorted.filter((r) => {
      const cuenta = (r.fields.CuentaContable_CC ?? '').toLowerCase();
      const cat = (r.fields.Categoria_CC ?? '').toLowerCase();
      return cuenta.includes(q) || cat.includes(q);
    });
  }, [rows, search, filterValue]);

  const columns: CrudColumn<CuentaContableItem>[] = [
    { header: 'Status', width: 'w-32', render: (r) => <StatusBadge status={r.fields.Status_CC} /> },
    {
      header: 'Cuenta Contable',
      render: (r) => (
        <span className="font-medium text-foreground">{r.fields.CuentaContable_CC || '—'}</span>
      ),
    },
    {
      header: 'Categoría',
      render: (r) => <span className="text-muted-foreground">{r.fields.Categoria_CC || '—'}</span>,
    },
    {
      header: 'EF Item',
      render: (r) => <span className="text-muted-foreground">{r.fields.EFItem_CC || '—'}</span>,
    },
    {
      header: 'EF SubItem',
      render: (r) => <span className="text-muted-foreground">{r.fields.EFSubItem_CC || '—'}</span>,
    },
    {
      header: 'Impacta Stock',
      render: (r) => (r.fields.ImpactaStock_CC === 'SI' ? 'Sí' : 'No'),
    },
    {
      header: 'Nro Serie',
      render: (r) => (r.fields.NroSerie_CC === 'SI' ? 'Sí' : 'No'),
    },
  ];

  const closeDialog = () => setMode({ kind: 'closed' });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    invalidateAbmRelatedQueries(queryClient, 'cuentas-contables');
  };

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin cuentas contables.'}
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canEdit={(r) => r.fields.Status_CC !== 'Inactivo'}
        editDisabledTitle={() => 'El registro está inactivo'}
        canDelete={(r) => r.fields.Status_CC !== 'Inactivo'}
        deleteDisabledTitle={() => 'El registro ya está inactivo'}
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <CuentaContableDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.kind === 'edit' ? mode.item : null}
          existingRows={rows}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}

      {mode.kind === 'delete' && (
        <DeleteCuentaDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.item}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

function CuentaContableDialog({
  open,
  onOpenChange,
  item,
  existingRows,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: CuentaContableItem | null;
  existingRows: CuentaContableItem[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const isEdit = Boolean(item);
  const { config: activeCountry } = useActiveCountry();
  // "Cuenta contable existente": en vez de re-tipear el nombre, se elige uno
  // que ya está. Una misma cuenta cuelga de VARIAS categorías a propósito
  // ("Gastos de Oficina" tiene cinco), así que agregar una combinación nueva
  // es lo normal — y escribir el nombre a mano para eso es justo donde un
  // typo crea una cuenta nueva en lugar de una categoría nueva.
  const [usarExistente, setUsarExistente] = useState(false);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: item ? itemToForm(item) : empty,
  });

  useEffect(() => {
    if (open) {
      form.reset(item ? itemToForm(item) : empty);
      setUsarExistente(false);
    }
  }, [open, item, form]);

  // Fetch active categorias for the lookup dropdown.
  const { data: categorias = [] } = useQuery({
    queryKey: ['configuracion', 'categorias', 'active', activeCountry.code],
    queryFn: listActiveCategorias,
    staleTime: 60_000,
  });

  // EF SubItems for the Estado Financiero classification dropdown. Active
  // only, sorted by EF Item then SubItem. AR-only data today → empty on MX.
  const { data: efSubitems = [] } = useQuery({
    queryKey: ['configuracion', 'ef-subitems', 'active', activeCountry.code],
    queryFn: listAllEfSubitems,
    staleTime: 60_000,
  });
  const efSubitemsActivos = useMemo(
    () =>
      efSubitems
        .filter((s) => s.fields.Status === 'Activo')
        .sort((a, b) =>
          `${a.fields.EfItemNombre ?? ''} ${a.fields.Nombre ?? ''}`.localeCompare(
            `${b.fields.EfItemNombre ?? ''} ${b.fields.Nombre ?? ''}`,
            'es',
          ),
        ),
    [efSubitems],
  );

  const mutation = useMutation({
    mutationFn: async (values: Values) => {
      const payload = {
        cuentaContable: values.cuentaContable,
        categoria: values.categoria || null,
        impactaStock: values.impactaStock,
        nroSerie: values.nroSerie,
        efSubItemId: values.efSubItemId || null,
      };
      if (isEdit && item) return updateCuentaContable(item.id, payload);
      return createCuentaContable(payload);
    },
    onSuccess: () =>
      onSuccess(
        isEdit
          ? 'Cuenta contable actualizada correctamente.'
          : 'Cuenta contable creada correctamente.',
      ),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo guardar la cuenta contable.'),
  });

  const onSubmit = form.handleSubmit((values) => {
    // La unicidad es del PAR (cuenta + categoría), no del nombre: el plan de
    // cuentas tiene 109 pares vivos sobre 75 nombres, o sea el nombre se
    // repite a propósito. La guarda vieja miraba sólo el nombre y por eso era
    // imposible dar de alta una categoría nueva para una cuenta que ya existe.
    const nombreNorm = values.cuentaContable.trim().toLowerCase();
    const catNorm = (values.categoria ?? '').trim().toLowerCase();
    // A propósito SIN filtrar por estado: si el par existe dado de baja y se
    // crea igual, quedan dos filas `gl_accounts` con el mismo (nombre,
    // categoría) y distinto `ef_subitem_id`. Como `cash_movements` se une al
    // plan de cuentas por ese par de TEXTO, la clasificación del Estado
    // Financiero de todo el histórico pasa a depender de cuál de las dos
    // filas gane el join. La base no tiene índice único que lo impida.
    const duplicated = existingRows.find(
      (r) =>
        r.id !== item?.id &&
        (r.fields.CuentaContable_CC ?? '').trim().toLowerCase() === nombreNorm &&
        (r.fields.Categoria_CC ?? '').trim().toLowerCase() === catNorm,
    );
    if (duplicated) {
      form.setError('cuentaContable', {
        type: 'manual',
        message:
          (duplicated.fields.Status_CC ?? '') === 'Activo'
            ? `"${values.cuentaContable.trim()}" ya está asociada a la categoría "${(values.categoria ?? '').trim()}".`
            : `Ese par ya existe en una cuenta dada de baja, que sigue clasificando movimientos históricos en el Estado Financiero. Crear otra igual dejaría esa clasificación ambigua: elegí otra categoría.`,
      });
      return;
    }
    mutation.mutate(values);
  });

  /**
   * Nombres de cuenta ya existentes, sin repetir, para el selector.
   *
   * OJO con el estado: la capa SharePoint mapea el `Eliminado` de la base a
   * `'Inactivo'` (`statusToSp`), así que `Status_CC` NUNCA vale 'Eliminado' y
   * filtrar por ese string no descarta nada. Por eso se exige `'Activo'`:
   * si no, las cuentas dadas de baja reaparecían en el selector y el cartel
   * de "Ya está en:" mostraba categorías retiradas — p. ej. "Accesorios de
   * Cocina · Mantenimiento", un par borrado cuando "Mantenimiento" se
   * reemplazó por "Reparacion y Mantenimiento".
   */
  const cuentasExistentes = useMemo(() => {
    const vistos = new Map<string, string>();
    for (const r of existingRows) {
      if ((r.fields.Status_CC ?? '') !== 'Activo') continue;
      const nombre = (r.fields.CuentaContable_CC ?? '').trim();
      if (!nombre) continue;
      const k = nombre.toLowerCase();
      if (!vistos.has(k)) vistos.set(k, nombre);
    }
    return [...vistos.values()].sort((a, b) => a.localeCompare(b, 'es'));
  }, [existingRows]);

  /** Las categorías que ya usa la cuenta elegida — para no repetir el par. */
  const categoriasDeLaCuenta = useMemo(() => {
    const nombre = form.getValues('cuentaContable').trim().toLowerCase();
    if (!nombre) return [] as string[];
    return existingRows
      .filter(
        (r) =>
          (r.fields.Status_CC ?? '') === 'Activo' &&
          (r.fields.CuentaContable_CC ?? '').trim().toLowerCase() === nombre,
      )
      .map((r) => (r.fields.Categoria_CC ?? '').trim())
      .filter(Boolean);
    // `watchedCuentaContable` la recalcula: se declara más abajo.
  }, [existingRows, form.watch('cuentaContable')]);

  const categoria = form.watch('categoria');
  const impactaStock = form.watch('impactaStock');
  const nroSerie = form.watch('nroSerie');
  const efSubItemId = form.watch('efSubItemId');
  const watchedCuentaContable = form.watch('cuentaContable');

  // The EF Item is derived from the picked EF SubItem (read-only display).
  const efItemDerivado = useMemo(
    () => efSubitemsActivos.find((s) => s.id === efSubItemId)?.fields.EfItemNombre ?? null,
    [efSubitemsActivos, efSubItemId],
  );

  /**
   * ¿El par (cuenta + categoría) ya existe?
   *
   * Se evalúa mientras el usuario elige, no al apretar Añadir: con el toggle
   * de "cuenta existente" la combinación repetida es el error natural del
   * flujo — se elige una cuenta que ya tiene categorías y se vuelve a marcar
   * una que ya estaba. Dejarlo para el submit obliga a completar todo el
   * formulario para recién ahí enterarse.
   */
  const parDuplicado = useMemo(() => {
    const nombreNorm = (watchedCuentaContable ?? '').trim().toLowerCase();
    const catNorm = (categoria ?? '').trim().toLowerCase();
    if (!nombreNorm || !catNorm) return null;
    // Sin filtro de estado — ver el comentario de la guarda de submit: un par
    // dado de baja recreado deja el join del Estado Financiero ambiguo.
    return (
      existingRows.find(
        (r) =>
          r.id !== item?.id &&
          (r.fields.CuentaContable_CC ?? '').trim().toLowerCase() === nombreNorm &&
          (r.fields.Categoria_CC ?? '').trim().toLowerCase() === catNorm,
      ) ?? null
    );
  }, [existingRows, item?.id, watchedCuentaContable, categoria]);

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!watchedCuentaContable?.trim()) missing.push('Cuenta Contable');
    // AR cuentas must carry a categoría + EF SubItem so future ventas/compras
    // booked against them have a derivable EF (mirrors the server-side guard).
    // MX accounts are EF-less by design.
    if (activeCountry.code === 'AR') {
      if (!categoria?.trim()) missing.push('Categoría');
      if (!efSubItemId) missing.push('EF SubItem');
    }
    return missing;
  }, [watchedCuentaContable, categoria, efSubItemId, activeCountry.code]);

  const canSubmit = missingFields.length === 0 && !parDuplicado;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-lg"
        processing={mutation.isPending && (isEdit ? 'Guardando cambios…' : 'Creando cuenta…')}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar cuenta contable' : 'Nueva cuenta contable'}
          </DialogTitle>
          <DialogDescription>
            Asociá la cuenta a una categoría y definí si impacta stock o número de serie.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor="cuentaContable">
                  Cuenta Contable <span className="text-primary">*</span>
                </Label>
                {!isEdit && cuentasExistentes.length > 0 && (
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <Switch
                      checked={usarExistente}
                      onCheckedChange={(v: boolean) => {
                        setUsarExistente(v);
                        // Cambiar de modo limpia el campo: lo tipeado a mano no
                        // tiene por qué existir, y viceversa.
                        form.setValue('cuentaContable', '', { shouldDirty: true });
                        form.clearErrors('cuentaContable');
                      }}
                    />
                    Cuenta contable existente
                  </label>
                )}
              </div>
              {usarExistente && !isEdit ? (
                <Combobox
                  items={cuentasExistentes}
                  value={watchedCuentaContable || null}
                  onChange={(n) =>
                    form.setValue('cuentaContable', n ?? '', { shouldDirty: true })
                  }
                  getKey={(n) => n}
                  getLabel={(n) => n}
                  matcher={(n, q) => n.toLowerCase().includes(q)}
                  placeholder="Seleccionar cuenta contable"
                />
              ) : (
                <Input
                  id="cuentaContable"
                  variant="filled"
                  // `filled` es h-12/text-[15px] y el Combobox es h-10/text-sm:
                  // sin esto el campo pegaba un salto al cambiar de modo y no
                  // alineaba con Categoría ni EF SubItem.
                  className="h-10 px-3 text-sm"
                  placeholder="Ingresar Cuenta Contable"
                  autoFocus
                  {...form.register('cuentaContable')}
                />
              )}
              {/* Una cuenta existente casi siempre ya tiene categorías: mostrarlas
                  evita elegir una que da un par repetido, que es el único error
                  que este alta puede cometer. */}
              {categoriasDeLaCuenta.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Ya está en:{' '}
                  <span className="text-foreground">{categoriasDeLaCuenta.join(' · ')}</span>
                </p>
              )}
              {form.formState.errors.cuentaContable && (
                <p className="text-xs font-medium text-destructive">
                  {form.formState.errors.cuentaContable.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Categoría</Label>
              <Combobox
                items={categorias}
                value={categorias.find((c) => (c.fields.Categoria_CAT ?? '') === categoria) ?? null}
                onChange={(c) =>
                  form.setValue('categoria', c?.fields.Categoria_CAT ?? '', { shouldDirty: true })
                }
                getKey={(c) => c.id}
                getLabel={(c) => c.fields.Categoria_CAT ?? '—'}
                matcher={(c, q) => (c.fields.Categoria_CAT ?? '').toLowerCase().includes(q)}
                placeholder="Seleccionar Categoría"
              />
            </div>

            <div className="space-y-1.5">
              <Label>EF SubItem</Label>
              <Combobox
                items={efSubitemsActivos}
                value={efSubitemsActivos.find((s) => s.id === efSubItemId) ?? null}
                onChange={(s) =>
                  form.setValue('efSubItemId', s?.id ?? '', { shouldDirty: true })
                }
                getKey={(s) => s.id}
                getLabel={(s) => `${s.fields.EfItemNombre ?? '—'} › ${s.fields.Nombre ?? '—'}`}
                matcher={(s, q) =>
                  `${s.fields.EfItemNombre ?? ''} ${s.fields.Nombre ?? ''}`.toLowerCase().includes(q)
                }
                placeholder={
                  efSubitemsActivos.length ? 'Seleccionar EF SubItem' : 'Sin EF SubItems disponibles'
                }
              />
              {/* The EF Item is implied by the SubItem — shown read-only.
                  Clear the picker (×) to leave the CC sin clasificación. */}
              <p className="text-xs text-muted-foreground">
                EF Item:{' '}
                <span className="font-medium text-foreground">{efItemDerivado || '—'}</span>
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <ToggleRow
                label="Impacta stock"
                description="La cuenta afecta el inventario."
                checked={impactaStock}
                onChange={(v) => form.setValue('impactaStock', v, { shouldDirty: true })}
              />
              <ToggleRow
                label="Número de serie"
                description="Los artículos de esta cuenta tienen serie."
                checked={nroSerie}
                onChange={(v) => form.setValue('nroSerie', v, { shouldDirty: true })}
              />
            </div>

            {/* Label de advertencia estándar, al pie del cuerpo — no en el
                renglón de los botones. El par repetido es el único error real
                que puede cometer esta alta: no se arregla completando lo que
                falta, así que se muestra apenas pasa. */}
            {parDuplicado && (
              <div className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <p>
                  <span className="font-medium">
                    &quot;{(watchedCuentaContable ?? '').trim()}&quot;
                  </span>{' '}
                  ya está asociada a la categoría{' '}
                  <span className="font-medium">
                    {parDuplicado.fields.Categoria_CC || '—'}
                  </span>
                  {(parDuplicado.fields.Status_CC ?? '') === 'Activo'
                    ? '. Elegí otra categoría.'
                    : ', en una cuenta dada de baja. Esa fila sigue clasificando movimientos históricos en el Estado Financiero, así que crear otra igual dejaría la clasificación ambigua. Elegí otra categoría.'}
                </p>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar:{' '}
                <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {isEdit ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border bg-secondary/40 p-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-primary"
      />
      <span className="flex flex-col">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </label>
  );
}

function DeleteCuentaDialog({
  open,
  onOpenChange,
  item,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: CuentaContableItem;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: async () => softDeleteCuentaContable(item.id),
    onSuccess: () => onSuccess('Cuenta contable eliminada correctamente.'),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo eliminar la cuenta contable.'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Eliminando cuenta…'}
      >
        <DialogHeader>
          <DialogTitle>Eliminar cuenta contable</DialogTitle>
          <DialogDescription>
            ¿Confirmás la eliminación de{' '}
            <strong className="text-foreground">{item.fields.CuentaContable_CC || '—'}</strong>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
